import React, { useCallback, useEffect, useState } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    Cloud,
    Clock,
    Link2,
    Loader2,
    Mail,
    RefreshCw,
    Sparkles,
    Wifi,
    WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { epostaMerkez } from "@/lib/api";
import EpostaOAuthWizard from "./EpostaOAuthWizard";

const REFRESH_MS = 30000;

function Metric({ label, value, accent = "sky" }) {
    const colors = {
        sky: "text-sky-400 border-sky-500/25 bg-sky-500/5",
        emerald: "text-emerald-400 border-emerald-500/25 bg-emerald-500/5",
        amber: "text-amber-400 border-amber-500/25 bg-amber-500/5",
        rose: "text-rose-400 border-rose-500/25 bg-rose-500/5",
    };
    return (
        <div className={`rounded-xl border p-3 ${colors[accent]?.split(" ").slice(2).join(" ") || ""}`}>
            <p className="text-[10px] uppercase text-slate-500">{label}</p>
            <p className={`text-xl font-bold mt-1 ${colors[accent]?.split(" ")[0]}`}>{value ?? "—"}</p>
        </div>
    );
}

export default function EpostaKomutaDashboard({ onSync, syncBusy }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await epostaMerkez.dashboard();
            setData(res);
            setLastRefresh(new Date());
        } catch {
            if (!silent) toast.error("Komuta verisi yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(() => load(true), REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    const oauthOk = data?.graph_oauth_ready;
    const bugun = data?.bugun || {};
    const hafta = data?.hafta || {};
    const logs = data?.sync_logs || {};
    const hotmail = data?.mailboxes?.hotmail || {};
    const gmail = data?.mailboxes?.gmail || {};

    async function handleRapor() {
        try {
            const res = await epostaMerkez.raporGonder();
            if (res.ok) toast.success(`Rapor gönderildi: ${(res.recipients || []).join(", ")}`);
            else toast.error(res.detail || "Rapor gönderilemedi.");
        } catch {
            toast.error("Rapor gönderilemedi.");
        }
    }

    if (loading && !data) {
        return (
            <div className="rounded-xl border border-white/10 p-8 text-center text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                Komuta merkezi yükleniyor…
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <section
                className={`rounded-2xl border p-5 ${
                    oauthOk
                        ? "border-sky-500/30 bg-gradient-to-br from-sky-950/30 to-[#0B0F19]"
                        : "border-amber-500/30 bg-gradient-to-br from-amber-950/20 to-[#0B0F19]"
                }`}
            >
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex gap-3">
                        <div className={`p-3 rounded-xl ${oauthOk ? "bg-sky-500/15" : "bg-amber-500/15"}`}>
                            {oauthOk ? <Wifi className="h-6 w-6 text-sky-400" /> : <WifiOff className="h-6 w-6 text-amber-400" />}
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-100">E-posta Komuta Merkezi</h2>
                            <p className="text-xs text-slate-400 mt-1">
                                Hotmail: {hotmail.email} · Gmail: {gmail.email}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                                <span
                                    className={`text-xs px-2 py-0.5 rounded-full border ${
                                        oauthOk
                                            ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
                                            : "border-amber-500/30 text-amber-300 bg-amber-500/10"
                                    }`}
                                >
                                    {oauthOk ? "Hotmail OAuth OK" : "OAuth gerekli"}
                                </span>
                                <span
                                    className={`text-xs px-2 py-0.5 rounded-full border ${
                                        data?.sync_enabled
                                            ? "border-emerald-500/30 text-emerald-300"
                                            : "border-slate-500/30 text-slate-400"
                                    }`}
                                >
                                    Otomatik sync {data?.sync_enabled ? "açık" : "kapalı"}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => load()}
                            className="h-8 px-3 rounded-lg border border-white/10 text-xs text-slate-300 hover:bg-white/5 inline-flex items-center gap-1"
                        >
                            <RefreshCw className="h-3.5 w-3.5" /> Yenile
                        </button>
                        <button
                            type="button"
                            onClick={onSync}
                            disabled={syncBusy}
                            className="h-8 px-3 rounded-lg bg-sky-600 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                        >
                            {syncBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Cloud className="h-3.5 w-3.5" />}
                            Senkron
                        </button>
                        <button
                            type="button"
                            onClick={handleRapor}
                            className="h-8 px-3 rounded-lg bg-violet-600 text-white text-xs font-semibold inline-flex items-center gap-1"
                        >
                            <Mail className="h-3.5 w-3.5" /> Haftalık rapor
                        </button>
                    </div>
                </div>
                {lastRefresh && (
                    <p className="text-[10px] text-slate-500 mt-3 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Son güncelleme {lastRefresh.toLocaleTimeString("tr-TR")} · {REFRESH_MS / 1000}s otomatik
                    </p>
                )}
            </section>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <Metric label="Bugün" value={bugun.toplam} accent="sky" />
                <Metric label="Bugün eşleşen" value={bugun.eslesen} accent="emerald" />
                <Metric label="Bekleyen" value={bugun.bekleyen} accent="amber" />
                <Metric label="Hafta toplam" value={hafta.toplam} accent="sky" />
                <Metric label="Hotmail bekleyen" value={hotmail.pending} accent="amber" />
                <Metric label="Gmail bekleyen" value={gmail.pending} accent="amber" />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <section className="lg:col-span-2 rounded-xl border border-white/10 bg-[#121A2F]/80 p-4">
                    <h3 className="text-sm font-semibold text-slate-200 mb-3">7 günlük trend</h3>
                    <div className="flex items-end gap-2 h-20">
                        {(data?.gunluk_seri || []).map((d) => {
                            const max = Math.max(...(data.gunluk_seri || []).map((x) => x.toplam), 1);
                            const h = Math.round((100 * d.toplam) / max);
                            return (
                                <div key={d.tarih} className="flex-1 flex flex-col items-center gap-1">
                                    <div className="w-full h-14 bg-black/20 rounded-t flex items-end justify-center">
                                        <div
                                            className="w-[70%] bg-sky-500 rounded-t"
                                            style={{ height: `${Math.max(h, 4)}%` }}
                                        />
                                    </div>
                                    <span className="text-[9px] text-slate-500">{d.tarih?.slice(-5)}</span>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                        Hafta eşleşme: %{hafta.eslesme_orani} · Takip kayıtları: {data?.takip_kayitlari}
                    </p>
                </section>

                <section className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4">
                    <h3 className="text-sm font-semibold text-slate-200 mb-3">Senkron sağlığı</h3>
                    {[
                        ["Hotmail", logs.hotmail],
                        ["Gmail", logs.gmail],
                        ["Birleşik", logs.all],
                    ].map(([label, log]) => (
                        <div key={label} className="py-2 border-b border-white/5 text-xs">
                            <div className="flex justify-between">
                                <span className="text-slate-400">{label}</span>
                                {log?.ok ? (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                                ) : (
                                    <AlertTriangle className="h-3.5 w-3.5 text-rose-400" />
                                )}
                            </div>
                            <p className="text-slate-500 mt-0.5">
                                {log?.zaman ? log.zaman.slice(0, 16) : "Henüz yok"} · {log?.processed || 0} işlendi
                            </p>
                            {log?.error && <p className="text-rose-300/80 text-[10px] mt-1 line-clamp-2">{log.error}</p>}
                        </div>
                    ))}
                    {data?.rapor_email?.length > 0 && (
                        <p className="text-[10px] text-slate-500 mt-3">Rapor: {data.rapor_email.join(", ")}</p>
                    )}
                </section>
            </div>

            <section className="rounded-xl border border-amber-500/20 bg-[#121A2F]/80 p-4">
                <h3 className="text-sm font-semibold text-amber-200 mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4" /> Bekleyen eşleşmeler
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                    {(data?.bekleyen_son || []).length === 0 ? (
                        <p className="text-xs text-slate-500">Bekleyen mail yok.</p>
                    ) : (
                        data.bekleyen_son.map((r) => (
                            <div key={r.id} className="text-xs border border-white/5 rounded p-2">
                                <div className="text-slate-200 truncate">{r.subject}</div>
                                <div className="text-slate-500">{r.from_email} · skor {r.match_score || "—"}</div>
                            </div>
                        ))
                    )}
                </div>
            </section>

            <EpostaOAuthWizard onConnected={() => load(true)} />
        </div>
    );
}
